/* eslint-disable */
import { defineStore } from "pinia";
import {Room, RoomEvent, Track} from "livekit-client";
import { markRaw } from "vue";
import { useGeneralStore } from "%/stores/general-store";

const BROADCAST_AUDIO_PRESET = { maxBitrate: BROADCAST_AUDIO_BITRATE, priority: "high" };
import VideoEngineError from "%/errors/video-engine-error";
import { isViewerParticipant } from "%/apps/video-engine/participant-roles";
import { appendBurst, isSingleEmoji, pruneBursts, BURST_TTL_MS } from "%/apps/video-engine/reaction-bursts";
import { BROADCAST_AUDIO_BITRATE, BROADCAST_VIDEO_BITRATE, REACTION_TOPIC, SFU_COMPATIBLE_ROOM_OPTIONS } from "%/apps/video-engine/constants";

function livekitServerUrl() {
  const serverUrl = useGeneralStore().livekitServerUrl;
  if (!serverUrl) {
    throw new VideoEngineError("livekit server url is not configured", {
      expectedLink: "livekitUrl",
      availableLinks: Object.keys(useGeneralStore().links || {}),
    });
  }
  return serverUrl;
}

export const useLivestreamStore = defineStore("livestream-store", {
  state: () => ({
    room: null,
    streamId: null,
    role: null,
    connected: false,
    connecting: false,
    error: null,

    localVideoTrack: null,
    localAudioTrack: null,
    remoteVideoTrack: null,
    remoteAudioTrack: null,
    reconnecting: false,
    connectionQuality: "unknown",
    deviceError: null,
    audienceCount: 0,
    audioPlaybackBlocked: false,
    reactionBursts: [],
    broadcastStatus: "idle",
  }),

  getters: {
    isBroadcasting() {
      return this.connected && this.role === 'broadcaster';
    },
    isViewing() {
      return this.connected && this.role === 'viewer';
    },
  },

  actions: {
    async connect(streamId, role) {
      if (this.connecting || (this.connected && this.streamId === streamId)) {
        return;
      }

      this.connecting = true;
      this.error = null;
      this.role = role;

      try {
        const token = await this._fetchToken(streamId, role);

        const roomOptions = {
          ...SFU_COMPATIBLE_ROOM_OPTIONS,
          adaptiveStream: true,
          dynacast: true,
        }

        if (role === 'broadcaster') {
          roomOptions.adaptiveStream = false;
          roomOptions.publishDefaults = {
            videoCodec: "h264",
            audioPreset: BROADCAST_AUDIO_PRESET,
            videoEncoding: {
              maxBitrate: BROADCAST_VIDEO_BITRATE,
              maxFramerate: 30,
            },
            dtx: false,
            red: true,
          };
          roomOptions.audioCaptureDefaults = {
            autoGainControl: true,
            echoCancellation: true,
            channelCount: 1,
            sampleRate: 48000,
          };
        }

        const room = markRaw(new Room(roomOptions));

        this.setupRoomListeners(room);
        await room.connect(livekitServerUrl(), token);

        this.room = room;
        this.streamId = streamId;
        this.connected = true;
        this.audioPlaybackBlocked = !room.canPlaybackAudio;
        this._syncAudienceCount(room);
        this._applyRoomMetadata(room.metadata);
        if (typeof window !== "undefined") {
          window.__livestreamStoreForTests = this;
        }

        if (role === 'broadcaster') {
          await this.startPublishing();
        }

      } catch (error) {
        console.error("[Livestream] Connection failed", error);
        this.error = error.message || String(error);
        await this.disconnect();
      } finally {
        this.connecting = false;
      }
    },

    async startPublishing() {
      if (!this.room) {
        throw new Error("cannot publish without a room");
      }
      try {
        await this.room.localParticipant.setCameraEnabled(true);
        await this.room.localParticipant.setMicrophoneEnabled(true);
      } catch (error) {
        console.error("[Livestream] Failed to publish media", error);
        this.error = "Could not access camera/microphone.";
      }
    },

    async endStreamForEveryone(eventId, occurrenceStart = 0) {
      if (!eventId) {
        await this.disconnect();

        return;
      }

      try {
        await this.apiPost(
          `/api/v1/event/${eventId}/end`,
          Number(occurrenceStart) > 0 ? { occurrence: Number(occurrenceStart) } : {},
          () => {},
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "[livestream] the event could not be ended on the server; viewers may still be watching",
          { eventId, error },
        );
      }

      await this.disconnect();
    },
    async disconnect() {
      if (this.room) {
        await this.room.disconnect();
      }
      this.room = null;
      this.connected = false;
      this.streamId = null;
      this.role = null;
      this.localVideoTrack = null;
      this.localAudioTrack = null;
      this.remoteVideoTrack = null;
      this.remoteAudioTrack = null;
      this.reconnecting = false;
      this.connectionQuality = "unknown";
      this.deviceError = null;
      this.audienceCount = 0;
      this.audioPlaybackBlocked = false;
      this.reactionBursts = [];
      this.broadcastStatus = "idle";
    },

    _applyRoomMetadata(metadata) {
      let decoded = {};
      try {
        decoded = JSON.parse(metadata || "{}") || {};
      } catch (error) {
        decoded = {};
      }
      this.broadcastStatus = ["idle", "live", "paused"].includes(decoded.broadcastStatus) ? decoded.broadcastStatus : "idle";
    },

    async sendReaction(emoji) {
      if (!isSingleEmoji(emoji)) {
        throw new Error(`[Livestream] A reaction must be a single emoji, got ${JSON.stringify(String(emoji).slice(0, 32))}`);
      }
      if (!this.streamId) {
        return;
      }
      await this.apiPost(`/api/v1/event/${this.streamId}/reaction`, { emoji }, () => {});
    },

    _showReaction(identity, emoji, name) {
      if (!identity) {
        return;
      }
      if (!isSingleEmoji(emoji)) {
        console.warn("[Livestream] Dropped a reaction that is not a single emoji", { // eslint-disable-line no-console
          identity,
          received: typeof emoji === "string" ? emoji.slice(0, 32) : typeof emoji,
        });
        return;
      }
      this.reactionBursts = appendBurst(this.reactionBursts, { identity, emoji, name: name || "" });
      setTimeout(() => {
        this.reactionBursts = pruneBursts(this.reactionBursts);
      }, BURST_TTL_MS + 100);
    },

    _handleDataMessage(payload, participant, topic) {
      if (topic !== REACTION_TOPIC) {
        return;
      }
      let message = {};
      try {
        message = JSON.parse(new TextDecoder().decode(payload));
      } catch (error) {
        return;
      }
      this._showReaction(participant?.identity || message.identity, message.emoji, participant?.name || message.name);
    },

    async resumeAudioPlayback() {
      if (!this.room) {
        return;
      }
      try {
        await this.room.startAudio();
        this.audioPlaybackBlocked = !this.room.canPlaybackAudio;
      } catch (error) {
        console.error("[Livestream] the browser still refuses audio playback", error);
      }
    },

    _syncAudienceCount(room) {
      const remoteViewers = Array.from(room.remoteParticipants.values()).filter(isViewerParticipant).length;
      this.audienceCount = remoteViewers + (this.role === "viewer" ? 1 : 0);
    },

    setupRoomListeners(room) {
      room.on(RoomEvent.AudioPlaybackStatusChanged, canPlay => {
        this.audioPlaybackBlocked = !canPlay;
      });
      room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
        this._handleDataMessage(payload, participant, topic);
      });
      room.on(RoomEvent.RoomMetadataChanged, metadata => {
        this._applyRoomMetadata(metadata);
      });

      room.on(RoomEvent.ParticipantConnected, () => {
        this._syncAudienceCount(room);
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        this._syncAudienceCount(room);
      });

      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === Track.Kind.Video) {
          this.remoteVideoTrack = track;
        } else if (track.kind === Track.Kind.Audio) {
          this.remoteAudioTrack = track;
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind === Track.Kind.Video) {
          this.remoteVideoTrack = null;
        }
        if (track.kind === Track.Kind.Audio) {
          this.remoteAudioTrack = null;
        }
      });

      room.on(RoomEvent.LocalTrackPublished, (publication) => {
        if (publication.track.kind === Track.Kind.Video) {
          this.localVideoTrack = publication.track;
        } else if (publication.track.kind === Track.Kind.Audio) {
          this.localAudioTrack = publication.track;
        }
      });

      room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
        if (publication.track?.kind === Track.Kind.Video) {
          this.localVideoTrack = null;
        }
        if (publication.track?.kind === Track.Kind.Audio) {
          this.localAudioTrack = null;
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        this.disconnect();
      });

      room.on(RoomEvent.Reconnecting, () => {
        this.reconnecting = true;
      });

      room.on(RoomEvent.Reconnected, () => {
        this.reconnecting = false;
      });

      room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        if (!participant || participant === room.localParticipant) {
          this.connectionQuality = quality;
        }
      });

      room.on(RoomEvent.MediaDevicesError, (error) => {
        this.deviceError = error?.message || String(error);
        console.error("[Livestream] media device error", error);
      });
    },

    async _fetchToken(eventId, mode) {
      let token;
      await this.apiGet(`/api/v1/event/${eventId}/livekit/token?mode=${mode}`, {}, (data) => {
        token = data.token;
      });

      return token;
    }
  }
});